import styled from '@emotion/styled';
import { css } from '@emotion/react';

export type StatusBadgeVariant = 'success' | 'warning' | 'error' | 'info';
export type StatusBadgeSize = 'small' | 'medium' | 'large';

type StyledProps = {
  $variant: StatusBadgeVariant;
  $size: StatusBadgeSize;
  $color?: string;
  $backgroundColor?: string;
  $borderColor?: string;
};

const palette: Record<
  StatusBadgeVariant,
  { bg: string; color: string; border: string }
> = {
  success: {
    bg: '#E6F7E6',
    color: '#2E7D32',
    border: '#4EC937',
  },
  warning: {
    bg: '#FFF4D6',
    color: '#B8860B',
    border: '#F0B01B',
  },
  error: {
    bg: '#FFE6E6',
    color: '#C62828',
    border: '#C10000',
  },
  info: {
    bg: '#E0F7FA',
    color: '#00838F',
    border: '#2180E8',
  },
};

const sizeStyles: Record<StatusBadgeSize, ReturnType<typeof css>> = {
  small: css`
    padding: 2px 6px;
    font-size: 11px;
    border-radius: 4px;
    gap: 4px;
    height: 18px;
  `,
  medium: css`
    padding: 4px 8px;
    font-size: 12px;
    border-radius: 6px;
    gap: 6px;
    height: 24px;
  `,
  large: css`
    padding: 6px 12px;
    font-size: 14px;
    border-radius: 8px;
    gap: 8px;
    height: 32px;
  `,
};

export const StyledStatusBadge = styled('span', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  font-family: 'Mangueira', sans-serif;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  border: 1px solid
    ${({ $borderColor, $variant }) =>
      $borderColor || palette[$variant].border};

  background-color: ${({ $backgroundColor, $variant }) =>
    $backgroundColor || palette[$variant].bg};
  color: ${({ $color, $variant }) => $color || palette[$variant].color};

  ${({ $size }) => sizeStyles[$size]}

  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
`;
